/**
 * Dashboard Data Hook
 * Aggregates data from multiple backend endpoints
 */

import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import type { DashboardData } from '../types/dashboard';

interface DashboardState {
  data: DashboardData | null;
  isLoading: boolean;
  error: string | null;
}

export function useDashboard(schoolId: string, academicYearId: string) {
  const [state, setState] = useState<DashboardState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    loadDashboardData();
  }, [schoolId, academicYearId]);

  const loadDashboardData = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      console.log('[Dashboard] Loading data for academic year:', academicYearId);

      // Build filters for year-dependent data
      const yearFilter = academicYearId ? { academic_year_id: academicYearId } : {};
      
      // Fetch all dashboard data in parallel
      const [
        dashboardResponse,
        birthdaysResponse,
        assessmentsResponse,
        feesStatsResponse,
      ] = await Promise.allSettled([
        apiService.getPrincipalDashboard(),
        apiService.getUpcomingBirthdays(),
        apiService.getAssessments(yearFilter),
        apiService.getFeesStats(yearFilter),
      ]);

      // Extract successful responses
      const dashboardData = dashboardResponse.status === 'fulfilled' ? dashboardResponse.value.data : null;
      const birthdays = birthdaysResponse.status === 'fulfilled' ? (birthdaysResponse.value.data || birthdaysResponse.value) : [];
      const assessments = assessmentsResponse.status === 'fulfilled' ? (assessmentsResponse.value.data || assessmentsResponse.value) : [];
      const feesStats = feesStatsResponse.status === 'fulfilled' ? feesStatsResponse.value.data : null;

      console.log('[Dashboard] Loaded:', {
        dashboardData,
        birthdays: (birthdays as any[]).length,
        assessments: (assessments as any[]).length,
      });

      // Log if dashboard fetch failed
      if (dashboardResponse.status === 'rejected') {
        console.error('[Dashboard] Failed to fetch dashboard:', dashboardResponse.reason);
        throw new Error(`Dashboard API error: ${dashboardResponse.reason?.message || 'Unknown error'}`);
      }

      if (!dashboardData) {
        throw new Error('Dashboard returned no data');
      }

      // Process assessments for progress
      const assessmentProgress = (assessments as any[])
        .filter((a: any) => a.published)
        .slice(0, 5)
        .map((assessment: any) => {
          const totalStudents = dashboardData.stats.totalStudents;
          const completedCount = assessment.marks?.length || 0;

          return {
            id: assessment.id,
            name: assessment.name,
            completed: completedCount,
            total: totalStudents,
          };
        });

      // Process birthdays
      const birthdayList = (birthdays as any[]).slice(0, 10).map((birthday: any) => {
        // Parse day and month from date_of_birth (format: YYYY-MM-DD)
        let day = 1;
        let month = 1;
        if (birthday.date_of_birth) {
          const dateParts = birthday.date_of_birth.split('-');
          if (dateParts.length >= 3) {
            month = parseInt(dateParts[1], 10);
            day = parseInt(dateParts[2], 10);
          }
        }

        return {
          id: birthday.user_id,
          name: birthday.full_name,
          type: birthday.role as 'student' | 'teacher',
          classroom: birthday.classroom,
          day,
          month,
        };
      });

      const finalDashboardData: DashboardData = {
        stats: dashboardData.stats,
        attendance: dashboardData.attendance,
        assessments: assessmentProgress,
        birthdays: birthdayList,
        currentAcademicYear: dashboardData.currentAcademicYear ? {
          id: dashboardData.currentAcademicYear.id,
          label: dashboardData.currentAcademicYear.label,
          status: dashboardData.currentAcademicYear.status as 'upcoming' | 'current' | 'closed',
          startDate: new Date(dashboardData.currentAcademicYear.starts_on),
          endDate: new Date(dashboardData.currentAcademicYear.ends_on),
          totalClasses: dashboardData.stats.totalClasses,
          totalStudents: dashboardData.stats.totalStudents,
        } : undefined,
        recentActivity: [], // Will be populated when audit endpoint is available
        fees: feesStats ? {
          totalCollected: feesStats.total_collected_paise,
          totalPending: feesStats.total_pending_paise,
          totalCharges: feesStats.total_charges_paise,
          chargeCount: feesStats.charge_count,
          paymentCount: feesStats.payment_count,
        } : undefined,
      };

      setState({ data: finalDashboardData, isLoading: false, error: null });
    } catch (error) {
      console.error('[Dashboard] Load error:', error);
      const message = error instanceof Error ? error.message : 'Failed to load dashboard data';
      setState({ data: null, isLoading: false, error: message });
    }
  };

  const retry = () => {
    loadDashboardData();
  };

  return {
    data: state.data,
    isLoading: state.isLoading,
    error: state.error,
    retry,
  };
}
